import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { sceneDisplayId } from 'js/src/agent/tools/scene-addressing';
import { sceneGroupDisplayId } from 'js/src/agent/tools/scene-group-addressing';
import type { ScenePartition } from 'js/src/agent/tools/transcript-partition';
import { partitionTranscriptByScene } from 'js/src/agent/tools/transcript-partition';
import { getAssetAppearanceOffset } from 'js/src/agent/video-state/serialization/assetTimingDescription';
import type { LintFinding } from '../videoLinter';
import type { ContentAsset } from './sceneContent';
import { editableContentAssets, hasContent, isMotionGraphicsScene } from './sceneContent';

import {
    getRootAssetGroup,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';

/** How long a scene may open on nothing but its background before it reads as a blank frame. */
export const LATE_CONTENT_THRESHOLD_MS = 500;

// Slack (ms) so a word the content was timed onto isn't counted as spoken before it over rounding.
const WORD_START_EPSILON_MS = 1;

const isOnLaterSubscene = (asset: ContentAsset): boolean =>
    VideoData.isSubscene(asset.luminaryParent) && asset.luminaryParent.index > 0;

/**
 * When an asset first shows, in milliseconds from the start of its scene. Luminary only applies
 * intro offsets on a scene's first subscene, so an asset on a later subscene appears with it.
 */
export function appearsAtInScene(asset: ContentAsset): number {
    if (isOnLaterSubscene(asset)) {
        return (asset.luminaryParent as { relativeStartTime: number }).relativeStartTime;
    }
    return getAssetAppearanceOffset({
        appliedIntroAnimationOffset: asset.appliedIntroAnimationOffset ?? 0,
        isMatchCutFrozen: asset.isMatchCutFrozen,
    });
}

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

/**
 * What an asset's timing is written through: set_text and set_icon take it directly, and anything
 * else sitting in a group moves with the group (set_group_asset). A standalone media, chart or
 * fancy caption has no tool that takes timing, so it comes back without one.
 */
function timingTarget(asset: ContentAsset): { name: string; tool?: string } {
    if (asset.classType === VideoData.TEXT_ASSET_TYPE) {
        return { name: asset.name ?? '', tool: TOOL_NAMES.setText };
    }
    if (asset.classType === VideoData.ICON_ASSET_TYPE) {
        return { name: asset.name ?? '', tool: TOOL_NAMES.setIcon };
    }
    const group = getRootAssetGroup(asset as unknown as Asset);
    if (group) {
        return { name: group.name, tool: TOOL_NAMES.setGroupAsset };
    }
    return { name: asset.name ?? '' };
}

/**
 * Bring every timed asset on the scene in `shiftMs` earlier, naming the call and new start for
 * each so the agent can't reach for a tool that doesn't take timing. Assets on later subscenes are
 * left alone — they appear with their subscene, not by offset.
 */
function describeShiftEarlier(assets: ContentAsset[], shiftMs: number): string {
    const targets = new Map<string, { tool?: string; startMs: number }>();
    assets
        .filter((asset) => !isOnLaterSubscene(asset) && appearsAtInScene(asset) > 0)
        .forEach((asset) => {
            const { name, tool } = timingTarget(asset);
            const startMs = Math.max(0, Math.round(appearsAtInScene(asset) - shiftMs));
            const existing = targets.get(name);
            targets.set(name, { tool, startMs: Math.min(existing?.startMs ?? startMs, startMs) });
        });

    const entries = [...targets.entries()];
    const retimable = entries.filter(([, target]) => target.tool);
    const stuck = entries.filter(([, target]) => !target.tool).map(([name]) => `"${name}"`);

    const retime = retimable
        .map(([name, { tool, startMs }]) => `"${name}" to start_time_ms ${startMs} (${tool})`)
        .join(', ');
    const sentences = [
        `Shift everything on this scene ${Math.round(shiftMs)}ms earlier, so the first asset appears ` +
            'with the scene and the rest keep their spacing' +
            (retime ? `: set ${retime}.` : '.'),
    ];
    if (stuck.length > 0) {
        sentences.push(
            `No tool takes timing for ${stuck.join(', ')}, so bringing ${stuck.length > 1 ? 'them' : 'it'} ` +
                `in earlier means rebuilding the scene (${TOOL_NAMES.deleteScene}, then ${TOOL_NAMES.addScene}).`,
        );
    }
    return sentences.join(' ');
}

/**
 * The fix for a voiceover overlay: start it later, on the word its first content lands on, so the
 * lead-in words go to something else. When the scene before is another overlay it is grown over
 * them. Otherwise this overlay is shrunk and the group's base shows through: fine on talking-head
 * and avatar groups, where that's the presenter, but on a plain voiceover it's waveform that
 * overlay_coverage rejects, so there the freed words also get a new overlay of their own. Either way
 * the assets keep their moment in the narration.
 *
 * Only when there are no words to hand over — the gap is silence before the first word, or the
 * content lands after the last — is the content itself brought in earlier.
 */
function describeVoiceoverFix(
    luminary: Luminary,
    sceneGroup: SceneGroup,
    partition: ScenePartition[],
    entryIndex: number,
    assets: ContentAsset[],
    firstContentVideoTime: number,
    leadInMs: number,
): string {
    const entry = partition[entryIndex];
    const firstContentTranscriptTime = translateVideoTimeToTranscriptTime(
        firstContentVideoTime,
        getVoiceDrivenAssetPlaybackRegions(sceneGroup),
        getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
        sceneGroup.startTime,
    );
    const firstKeptTokenIndex = entry.tokens.findIndex(
        (token) => token.startTime >= firstContentTranscriptTime - WORD_START_EPSILON_MS,
    );
    const leadInCount = firstKeptTokenIndex === -1 ? entry.tokens.length : firstKeptTokenIndex;
    if (leadInCount === 0 || leadInCount === entry.tokens.length) {
        return (
            "The gap isn't covered by any words this scene could hand to the scene before it. " +
            describeShiftEarlier(assets, leadInMs)
        );
    }

    const leadInWords = entry.words.slice(0, leadInCount);
    const keptWords = entry.words.slice(leadInCount);
    const opensOn = keptWords[0];
    const keepTiming = "Don't pull the content off its words; start the scene later instead.";
    const outcome = `This scene then starts at "${opensOn}", and its assets stay on the words they are timed to.`;

    const previous = partition[entryIndex - 1];
    if (previous?.role === 'overlay') {
        const previousId = sceneDisplayId(luminary, previous.scene.id);
        return (
            `${keepTiming} Extend the previous scene ${previousId} with ${TOOL_NAMES.setSceneRange} so it ` +
            `also covers "${leadInWords.join(' ')}" (words: ${JSON.stringify([...previous.words, ...leadInWords])}). ` +
            outcome
        );
    }

    const shrink =
        `${keepTiming} Re-time this scene with ${TOOL_NAMES.setSceneRange} so it starts at ` +
        `"${opensOn}" (words: ${JSON.stringify(keptWords)})`;
    // Whether there's a scene before this one or not, the base it exposes is the group's: the
    // presenter on talking-head and avatar groups, the waveform only on a plain voiceover.
    if (!isVoiceoverSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
        return `${shrink}; the presenter shows through "${leadInWords.join(' ')}". ${outcome}`;
    }
    // The base here is the voiceover waveform, which overlay_coverage rejects wherever it shows, so
    // the freed words need a scene of their own — together with any already uncovered before them.
    const gapWords = [...(previous?.role === 'uncovered' ? previous.words : []), ...leadInWords];
    return (
        `${shrink}, then cover "${gapWords.join(' ')}" with a new scene (${TOOL_NAMES.addVoiceoverScene}, ` +
        `scene_group_id "${sceneGroupDisplayId(luminary, sceneGroup.id)}", words: ${JSON.stringify(gapWords)}) ` +
        `so the waveform doesn't show through. ${outcome}`
    );
}

/**
 * Error: every piece of content on a scene arrives well after the scene starts, so it opens on an
 * empty background. Timing an asset to the word that introduces it is good practice, but when the
 * scene starts long before that word, the viewer is left looking at a blank frame (the scene is
 * starting too early, not the asset appearing too late).
 *
 * Only editable, filled assets are judged — a design's locked decoration doesn't carry the scene,
 * and an empty frame is blank_scene's concern. Motion-graphics scenes and a voice-driven group's
 * base/presenter scenes are skipped: the former animate themselves, and the latter always show the
 * presenter or waveform.
 *
 * On a voiceover the suggested fix moves the scene's start later rather than pulling the asset off
 * the word it was deliberately timed to. A text-on-media scene has no narration to stay in sync
 * with, so there the fix is to shift its content earlier.
 */
export function checkLateSceneContent(luminary: Luminary, getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];
    // Partitioning re-reads the group's whole transcript, so do it once per group, not per scene.
    const partitions = new Map<string, ScenePartition[] | null>();
    const partitionOf = (sceneGroup: SceneGroup) => {
        if (!partitions.has(sceneGroup.id)) {
            partitions.set(sceneGroup.id, partitionTranscriptByScene(getState(), sceneGroup));
        }
        return partitions.get(sceneGroup.id) ?? null;
    };

    luminary.scenes.forEach((scene: Scene) => {
        if (isMotionGraphicsScene(scene)) {
            return;
        }

        const sceneGroup = scene.sceneGroup;
        const voiceDrivenGroup =
            sceneGroup && isVoiceDrivenSceneGroup(sceneGroup) ? sceneGroup : null;
        const partition = voiceDrivenGroup ? partitionOf(voiceDrivenGroup) : null;
        const entryIndex = partition?.findIndex((part) => part.scene.id === scene.id) ?? -1;
        if (voiceDrivenGroup && partition?.[entryIndex]?.role !== 'overlay') {
            return;
        }

        const filled = editableContentAssets(scene).filter(hasContent);
        if (filled.length === 0) {
            return;
        }
        const first = filled.reduce((earliest, asset) =>
            appearsAtInScene(asset) < appearsAtInScene(earliest) ? asset : earliest,
        );
        const leadInMs = appearsAtInScene(first);
        if (leadInMs <= LATE_CONTENT_THRESHOLD_MS) {
            return;
        }

        const fix =
            voiceDrivenGroup && partition
                ? describeVoiceoverFix(
                      luminary,
                      voiceDrivenGroup,
                      partition,
                      entryIndex,
                      filled,
                      scene.startTime + leadInMs,
                      leadInMs,
                  )
                : describeShiftEarlier(filled, leadInMs);

        findings.push({
            check: 'late_scene_content',
            severity: 'error',
            sceneId: scene.id,
            message:
                `Nothing is on screen for the first ${seconds(leadInMs)} of this scene — its earliest ` +
                `content ("${first.name}") only appears ${seconds(leadInMs)} in, so the scene opens on ` +
                `an empty background. ${fix}`,
        });
    });

    return findings;
}
